import { errorLog, notify } from '@/utils'
import { useApp } from './app'
import { createStore } from './util'
import {
  SignUpRequest,
  LoginRequest,
  VerificationRequest,
  User,
  PointsResponse,
  LoginResponseData,
  APIResponse,
  PointsSummary,
  ForgotPasswordRequest,
  PasswordResetRequest,
  Referral,
  EpochPoints,
} from '@/types'

interface UserStore {
  token: string | null
  user: User | null
  signUp: (data: SignUpRequest, turnstileToken: string) => Promise<void>
  login: (data: LoginRequest, turnstileToken: string) => Promise<void>
  logout: () => Promise<void>
  getPointsSummary: () => Promise<PointsSummary | null>
  getPoints: (interval: number) => Promise<PointsResponse | null>
  getPointsByEpochs: (epochs: number) => Promise<EpochPoints[] | null>
  getReferrals: () => Promise<Referral[] | null>
  verify: (data: VerificationRequest) => Promise<void>
  forgotPassword: (data: ForgotPasswordRequest, turnstileToken: string) => Promise<void>
  resetPassword: (data: PasswordResetRequest, turnstileToken: string) => Promise<void>
  initFromStorage: () => void
  clearToken: () => void
}

export const useUser = createStore<UserStore>(
  'user',
  (set, get) => ({
    token: null,
    user: null,
    signUp: async (data, turnstileToken) => {
      const http = useApp.getState().http

      // clear account
      get().clearToken()

      try {
        const response = await http.post<APIResponse<null>>(`api/v3/dashboard/auth/signup`, data, {
          headers: {
            'cf-turnstile-response': turnstileToken,
          },
        })

        if (!response.data) {
          console.log('[signUp] No data returned from server')
          throw 'No data returned from server'
        }

        if (!response.data.success) {
          console.log('[signUp] Response is not success', response.data.message)
          throw response.data.message
        }

        notify.success(response.data.message)
      } catch (error) {
        errorLog(error)
      }
    },
    login: async (data, turnstileToken) => {
      const http = useApp.getState().http

      // clear account
      get().clearToken()

      try {
        const response = await http.post<APIResponse<LoginResponseData>>(
          `api/v3/dashboard/auth/login`,
          data,
          {
            headers: {
              'cf-turnstile-response': turnstileToken,
            },
          },
        )

        if (response.data.success) {
          set({
            token: response.data.data.token,
            user: response.data.data.user,
          })

          window.localStorage.setItem(
            'user',
            JSON.stringify({
              token: response.data.data.token,
              user: response.data.data.user,
            }),
          )
        }
      } catch (error) {
        errorLog(error)
      }
    },
    logout: async () => {
      const token = get().token
      const http = useApp.getState().http

      if (!token) {
        console.log('[logout] No token found')
        return
      }

      try {
        const response = await http.post<APIResponse<null>>(
          'api/v3/dashboard/auth/logout',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.data.success) {
          console.log('[logout] Response is not success', response.data.message)
          throw response.data.message
        }

        // Only clear tokens after successful logout
        get().clearToken()
        notify.success('Logged out successfully')
      } catch (error) {
        errorLog(error)
      }
    },
    getPointsSummary: async (): Promise<PointsSummary | null> => {
      const token = get().token
      const http = useApp.getState().http

      if (!token) {
        console.log('[getPointsSummary] No token found')
        return null
      }

      try {
        const response = await http.get<APIResponse<PointsSummary>>(
          'api/v3/dashboard/points/summary',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.data) {
          console.log('[getPointsSummary] No data returned from server')
          throw 'No data returned from server'
        }

        if (!response.data.data) {
          console.log('[getPointsSummary] No data in response')
          throw 'No data in response'
        }

        if (!response.data.success) {
          console.log('[getPointsSummary] Request failed:', response.data.message)
          throw response.data.message
        }

        return response.data.data
      } catch (error) {
        errorLog(error)
        return null
      }
    },
    getPoints: async (interval: number): Promise<PointsResponse | null> => {
      const token = get().token
      const http = useApp.getState().http

      if (!token) {
        console.log('[getPoints] No token found')
        return null
      }

      try {
        const response = await http.get<APIResponse<PointsResponse>>(
          `api/v3/dashboard/points?last_days=${interval}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.data) {
          console.log('[getPoints] No data returned from server')
          throw new Error('No data returned from server')
        }

        if (!response.data.success) {
          console.log('[getPoints] Request failed:', response.data.message)
          throw response.data.message
        }

        if (!response.data.data) {
          console.log('[getPoints] No points data in response')
          throw new Error('No points data in response')
        }

        return response.data.data
      } catch (error) {
        errorLog(error)
        return null
      }
    },
    getPointsByEpochs: async (epochs: number): Promise<EpochPoints[] | null> => {
      const token = get().token
      const http = useApp.getState().http

      if (!token) {
        console.log('[getEpochs] No token found')
        return null
      }

      try {
        const response = await http.get<APIResponse<PointsResponse>>(
          `api/v3/dashboard/points?epochs=${epochs}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.data) {
          console.log('[getEpochs] No data returned from server')
          throw 'No data returned from server'
        }

        if (!response.data.success) {
          console.log('[getEpochs] Request failed:', response.data.message)
          throw response.data.message
        }

        return response.data.data.epochs || null
      } catch (error) {
        errorLog(error)
        return null
      }
    },
    getReferrals: async (): Promise<Referral[] | null> => {
      const token = get().token
      const http = useApp.getState().http

      if (!token) {
        console.log('[getReferrals] No token found')
        return null
      }

      try {
        const response = await http.get<APIResponse<Referral[]>>('api/v3/dashboard/referrals', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.data) {
          console.log('[getReferrals] No data returned from server')
          throw 'No data returned from server'
        }

        if (!response.data.success) {
          console.log('[getReferrals] Request failed:', response.data.message)
          throw response.data.message
        }

        return response.data.data
      } catch (error) {
        errorLog(error)
        return null
      }
    },
    verify: async (data: VerificationRequest) => {
      const http = useApp.getState().http

      try {
        const response = await http.post<APIResponse<LoginResponseData>>(
          'api/v3/dashboard/auth/verify',
          data,
        )

        if (!response.data?.success || !response.data.data) {
          throw new Error(response.data?.message || 'Verification failed')
        }

        const { token, user } = response.data.data

        // Store user data just like login
        window.localStorage.setItem('user', JSON.stringify({ token, user }))
        set({ token, user })

        notify.success('Email verified successfully')
      } catch (error) {
        errorLog(error)
        throw error
      }
    },
    forgotPassword: async (data: ForgotPasswordRequest, turnstileToken: string) => {
      const http = useApp.getState().http

      try {
        const response = await http.post<APIResponse<null>>(
          'api/v3/dashboard/auth/forgot-password',
          data,
          {
            headers: {
              'cf-turnstile-response': turnstileToken,
            },
          },
        )

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Password recovery failed')
        }

        notify.success(response.data?.message)
      } catch (error) {
        errorLog(error)
      }
    },
    resetPassword: async (data: PasswordResetRequest, turnstileToken: string) => {
      const http = useApp.getState().http

      try {
        const response = await http.post<APIResponse<null>>(
          'api/v3/dashboard/auth/reset-password',
          data,
          {
            headers: {
              'cf-turnstile-response': turnstileToken,
            },
          },
        )

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Password reset failed')
        }

        notify.success('Password reset successfully')
      } catch (error) {
        errorLog(error)
      }
    },
    initFromStorage: () => {
      const stored = window.localStorage.getItem('user')
      if (!stored) return

      try {
        const { token, user } = JSON.parse(stored)
        set({ token, user })
      } catch {
        console.error('[initFromStorage] Failed to parse stored user')
        window.localStorage.removeItem('user')
      }
    },
    clearToken: () => {
      set({ token: null, user: null })
      window.localStorage.removeItem('user')
    },
  }),
  false,
)
